import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronDown } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Button,
  itemVariants,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

type Props = {
  additional?: NonNullable<MediaPlan.FlightsList['data']>['additional'];
  handleCreateFlights: () => void;
};

const NewFlight: React.FC<Props> = ({ additional, handleCreateFlights }) => {
  const { t } = useTranslation('@myn/planning_v2');

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const { customFlightCanBeCreated } = additional || {};

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleCustomClick = () => {
    setIsPopoverOpen(false);
    handleCreateFlights();
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          size="medium"
          color="secondary"
          aria-label="Add new flight"
          disabled={isReadOnly}
        >
          <ChevronDown />
          {t('New flight')}
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent className={cn('z-popover flex justify-center p-0')}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={handleCustomClick}
                  disabled={!customFlightCanBeCreated}
                  aria-label="Add custom flight"
                  variant="text"
                  className={itemVariants({ popoverItem: 'default' })}
                >
                  {t('Custom')}
                </Button>
              </span>
            </TooltipTrigger>

            {!customFlightCanBeCreated && (
              <TooltipContent className="z-tooltip">
                {t(
                  'There are flights that already cover this time portion of the activity',
                )}
              </TooltipContent>
            )}
          </Tooltip>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export default NewFlight;
